import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchBar from "../../components/SearchBar";
import Pagination from "../../components/Panigation";
import {
  useLazyGetDocumentsQuery,
  useVerifyDocumentsMutation,
} from "../../service/documents";
import { showError, showToast } from "../../constants/toasts";

type DocumentsStatus = 1 | 2 | 3;

type DocumentRow = {
  _id: string;
  referenceId: string;
  email: string;
  fullName: string;
  phone: string;
  countryCode: string;
  documentsStatus: DocumentsStatus;
  createdAt: string;
  TINNumber?: string;
  businessCertificate?: string;
  businessRegNo?: string;
  natioalIdCard?: string;
};

const STATUS_LABEL: Record<number, string> = {
  1: "Pending",
  2: "Accepted",
  3: "Rejected",
};

const getStatusColor = (status: DocumentsStatus) => {
  if (status === 2) return "success";
  if (status === 3) return "error";
  return "warning";
};

const DOCUMENT_FIELDS = [
  { key: "businessCertificate", label: "Business Certificate" },
  { key: "businessRegNo", label: "Business Reg No" },
  { key: "natioalIdCard", label: "National ID Card" },
  { key: "TINNumber", label: "TIN Number" },
] as const;

const ManageDocuments = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DocumentRow | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [actionRow, setActionRow] = useState<DocumentRow | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const [getDocuments, { isFetching }] = useLazyGetDocumentsQuery();
  const [verifyDocuments] = useVerifyDocumentsMutation();

  const totalPages = Math.ceil(totalCount / limit);

  const visibleDocsCount = useMemo(
    () =>
      DOCUMENT_FIELDS.filter((field) => {
        const v = selectedRow?.[field.key];
        return typeof v === "string" && v.trim().length > 0;
      }).length,
    [selectedRow]
  );

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await getDocuments({
        page,
        limit,
        search: searchTerm || undefined,
      }).unwrap();

      if (response?.status) {
        setDocuments(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      } else {
        setDocuments([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      setDocuments([]);
      setTotalCount(0);
      showError(error?.data?.message || "Failed to fetch documents");
    }
  }, [getDocuments, page, limit, searchTerm]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const openViewModal = (row: DocumentRow) => {
    setSelectedRow(row);
    setViewOpen(true);
  };

  const closeViewModal = () => {
    setSelectedRow(null);
    setViewOpen(false);
  };

  const handleUpdateDocumentStatus = async (
    id: string,
    documentsStatus: 2 | 3,
    documentRejectReason?: string
  ) => {
    try {
      setUpdatingId(id);
      const response = await verifyDocuments({
        id,
        body: {
          documentsStatus,
          ...(documentsStatus === 3 && documentRejectReason
            ? { documentRejectReason }
            : {}),
        },
      }).unwrap();
      showToast(
        response?.message ||
          `Documents ${documentsStatus === 2 ? "accepted" : "rejected"} successfully`
      );

      if (selectedRow?._id === id) {
        setSelectedRow((prev) =>
          prev ? { ...prev, documentsStatus: documentsStatus } : prev
        );
      }

      // After successful action, close all modals and reset action state.
      setViewOpen(false);
      setConfirmApproveOpen(false);
      setConfirmRejectOpen(false);
      setSelectedRow(null);
      setActionRow(null);
      setRejectReason("");

      fetchDocuments();
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update documents status");
    } finally {
      setUpdatingId(null);
    }
  };

  const openApproveModal = (row: DocumentRow) => {
    setActionRow(row);
    setConfirmApproveOpen(true);
  };

  const openRejectModal = (row: DocumentRow) => {
    setActionRow(row);
    setRejectReason("");
    setConfirmRejectOpen(true);
  };

  const closeApproveModal = () => {
    setConfirmApproveOpen(false);
    setActionRow(null);
  };

  const closeRejectModal = () => {
    setConfirmRejectOpen(false);
    setActionRow(null);
    setRejectReason("");
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Documents</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Documents
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(value) => {
                setPage(1);
                setSearchTerm(value);
              }}
              placeholder="Search by name, email or reference id..."
            />
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Reference ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Uploaded Docs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((row) => (
                <TableRow hover key={row._id}>
                  <TableCell>{row.referenceId || "-"}</TableCell>
                  <TableCell>
                    <div className="user_block">
                      <p>
                        <b>{row.fullName || "-"}</b>
                        <span>{row.email || "-"}</span>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.countryCode || ""}
                    {row.phone || "-"}
                  </TableCell>
                  <TableCell>
                    {
                      DOCUMENT_FIELDS.filter((field) => {
                        const value = row[field.key];
                        return (
                          typeof value === "string" && value.trim().length > 0
                        );
                      }).length
                    }{" "}
                    / 4
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABEL[row.documentsStatus] || "Pending"}
                      color={getStatusColor(row.documentsStatus) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="btn_flex">
                      {row.documentsStatus !== 2 && (
                        <Button
                          className="btn btn_secondary"
                          size="small"
                          onClick={() => openApproveModal(row)}
                          disabled={updatingId === row._id}
                        >
                          Accept
                        </Button>
                      )}

                      {row.documentsStatus !== 3 && (
                        <Button
                          className="btn btn_danger"
                          size="small"
                          onClick={() => openRejectModal(row)}
                          disabled={updatingId === row._id}
                        >
                          Reject
                        </Button>
                      )}

                      <div className="table_actions v2">
                        <IconButton onClick={() => openViewModal(row)}>
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}

              {!isFetching && documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2">No documents found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <Pagination
            module={documents}
            page={page}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            setPage={setPage}
            totalCount={totalCount}
          />
        )}
      </div>

      <Dialog open={viewOpen} onClose={closeViewModal} maxWidth="md" fullWidth>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              {selectedRow?.fullName || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedRow?.email || "-"} | {selectedRow?.referenceId || "-"}
            </Typography>
            <Box mt={1}>
              <Chip
                label={STATUS_LABEL[selectedRow?.documentsStatus || 1]}
                color={getStatusColor(selectedRow?.documentsStatus || 1) as any}
                size="small"
              />
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Uploaded documents: {visibleDocsCount} / 4
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {DOCUMENT_FIELDS.map((field) => {
              const url = selectedRow?.[field.key]?.trim();
              return (
                <Box
                  key={field.key}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    p: 2,
                    minHeight: 170,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {field.label}
                  </Typography>

                  {url ? (
                    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>
                      {url.toLowerCase().includes('.pdf') ? (
                        <Box sx={{
                          width: "100%",
                          height: 120,
                          backgroundColor: "#fff0f0",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          color: "#d32f2f",
                          border: "1px solid #f9bdbb"
                        }}>
                          <PictureAsPdfIcon sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="body2" fontWeight={600} color="text.secondary">PDF Document</Typography>
                        </Box>
                      ) : (
                        <img
                          src={url}
                          alt={field.label}
                          style={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      )}
                    </Link>
                  ) : (
                    <Box
                      sx={{
                        height: 120,
                        borderRadius: 2,
                        backgroundColor: "#f7f7f7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#888",
                        fontSize: 14,
                      }}
                    >
                      Not uploaded
                    </Box>
                  )}

                  {url && (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ mt: 1, display: "inline-block" }}
                    >
                      Open full {url.toLowerCase().includes('.pdf') ? 'document' : 'image'}
                    </Link>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeViewModal}>Close</Button>
          {selectedRow && selectedRow.documentsStatus !== 2 && (
            <Button
              className="btn btn_secondary"
              onClick={() => openApproveModal(selectedRow)}
              disabled={updatingId === selectedRow._id}
            >
              Accept
            </Button>
          )}
          {selectedRow && selectedRow.documentsStatus !== 3 && (
            <Button
              className="btn btn_danger"
              onClick={() => openRejectModal(selectedRow)}
              disabled={updatingId === selectedRow._id}
            >
              Reject
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmApproveOpen} onClose={closeApproveModal} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Documents</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to approve these documents?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveModal}>Cancel</Button>
          <Button
            className="btn btn_secondary"
            disabled={!actionRow || updatingId === actionRow?._id}
            onClick={async () => {
              if (!actionRow) return;
              await handleUpdateDocumentStatus(actionRow._id, 2);
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRejectOpen} onClose={closeRejectModal} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Documents</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to reject these documents?
          </Typography>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason"
            rows={4}
            style={{
              width: "100%",
              border: "1px solid #d7d7d7",
              borderRadius: 8,
              padding: 10,
              fontFamily: "inherit",
              fontSize: 14,
              resize: "vertical",
            }}
          />
          {!rejectReason.trim() && (
            <Typography variant="caption" color="error">
              Rejection reason is required.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectModal}>Cancel</Button>
          <Button
            className="btn btn_danger"
            disabled={
              !actionRow || !rejectReason.trim() || updatingId === actionRow?._id
            }
            onClick={async () => {
              if (!actionRow || !rejectReason.trim()) return;
              await handleUpdateDocumentStatus(
                actionRow._id,
                3,
                rejectReason.trim()
              );
            }}
            style={{color: "white"}}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageDocuments;
